import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { SimpleModal } from '@sequencehq/core-components'
import { useState } from 'react'
import { useNotifications } from '@sequencehq/core-components'
import { UsageMetricForm, UsageMetricInput } from './UsageMetricForm'

interface Props {
  usageMetricId: string
  isOpen?: boolean
  onClose: () => void
  onUpdate?: () => void
  closeOnUpdate?: boolean
}

export const EditUsageMetricDrawer = ({
  usageMetricId,
  isOpen = true,
  onClose,
  onUpdate,
  closeOnUpdate
}: Props) => {
  const usageMetricQuery = useQuery(dashboard20240730Client.getUsageMetric, {
    id: usageMetricId
  })
  const updateMetricMutation = useMutation(
    dashboard20240730Client.putUsageMetric
  )
  const { displayNotification } = useNotifications()
  const [formData, setFormData] = useState<UsageMetricInput | null>(null)
  const [formIsValid, setFormIsValid] = useState(false)
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const handleSubmit = async () => {
    if (!formData) return false
    if (!formIsValid) {
      setShowValidationErrors(true)
      return false
    }

    try {
      const response = await updateMetricMutation.mutateAsync({
        id: usageMetricId,
        body: {
          ...formData,
          propertiesToNegate: formData.propertiesToNegate || [],
          propertyFilters: formData.propertyFilters || {}
        }
      })

      if (response) {
        displayNotification('Usage metric updated successfully', {
          type: 'success'
        })

        onUpdate?.()
        if (closeOnUpdate) {
          onClose()
        }
        return true
      }

      displayNotification('Failed to update usage metric', {
        type: 'error'
      })
      return false
    } catch {
      displayNotification('Failed to update usage metric', {
        type: 'error'
      })
      return false
    }
  }

  return (
    <SimpleModal
      data-testid="usageMetric.drawer"
      isOpen={isOpen}
      onClose={onClose}
      variant="drawer"
      title="Edit usage metric"
      cancelButtonText="Cancel"
      submitButtonText="Update usage metric"
      submittingButtonText="Updating usage metric..."
      submitDisabled={updateMetricMutation.isPending}
      onSubmit={handleSubmit}
      isLoading={usageMetricQuery.isPending}
      closeOnUpdate={closeOnUpdate}
    >
      {usageMetricQuery.data && (
        <UsageMetricForm
          initialValues={usageMetricQuery.data}
          onChange={setFormData}
          onValidationChange={setFormIsValid}
          showValidationErrors={showValidationErrors}
        />
      )}
    </SimpleModal>
  )
}
